<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'
  import { useBuilderStore } from '@/store/builder'
  import {
    Copy01Icon,
    DotsHorizontalIcon,
    Settings01Icon,
    Trash02Icon,
    PlusIcon,
    Upload01Icon,
    ImageUpIcon,
  } from '@gohighlevel/ghl-icons/24/outline'
  import { renderIcon, UIButton, UIDropdown } from '@gohighlevel/ghl-ui'
  import { computed, ref, Ref } from 'vue'
  import PageRenderer from './PageRenderer.vue'
  import { IElement, WithCommonProvider, AddToContentLibraryModal, CONTENT_LIBRARY, featureFlags } from '@gohighlevel/ghl-proposals-common'
  import { usePropertiesDrawer } from '@/composable/usePropertiesDrawer'
  import { SelectoComponentWrapper } from '@gohighlevel/ghl-proposals-common'
  import { useGroups } from '@/composition/useGroups'
  import AppendedFileUploadModal from '../common/AppendedFileUploadModal.vue'
  import { useRoute } from 'vue-router'
  import { useElementOption } from '@/composable/elementOptions'
  import { useAppStore } from '@/store/app'
  import { useDocument } from '../../composition'
  import { useActionBarStore } from '@/store/actionBar'

  const route = useRoute()
  const appStore = useAppStore()
  const locationId = computed(() => route.params.locationId)
  const { t } = useI18n()
  const store = useBuilderStore()
  const {
    groupTargets,
    unGroupTargets,
    deleteGroupElements,
    cloneGroupElements,
    setGroupTargetsOnSelection,
    onCloneAndAddToGroupByGroupId,
  } = useGroups()
  const { setOpen } = usePropertiesDrawer()
  const pages = computed(() => store.pages)
  const activeGroup = computed(() => store.activeGroup)
  const pageWrapperRef = ref<HTMLElement | null>()
  const appendImagesFromIndex = ref(0)
  const showUploadModal = ref(false)
  const SelectoFieldComponent = WithCommonProvider(SelectoComponentWrapper)
  const { updateStyles, updateOption } = useElementOption()
  const showAddToContentLibraryModal = ref(false)
  const activePage: Ref<IElement | null> = ref(null)
  const { documentVersion } = useDocument()
  const actionBarStore = useActionBarStore()
  const isDragOver = ref(false)
  const dragOverIndex = ref(-1)

  const getOptions = (page: IElement) => {
    const deleteOption = {
      label: t('common.deletePage'),
      key: 'delete_page',
      icon: renderIcon(Trash02Icon),
    }

    const addPdfOption = {
      label: t('common.addPdfs'),
      key: 'add_pdf',
      icon: renderIcon(Upload01Icon),
    }

    const addToContentLibraryOption = {
      label: t('contentLibrary.addToContentLib'),
      key: 'export_to_content_library',
      icon: renderIcon(ImageUpIcon),
    }

    if (page.component.options.fromPDF) {
      return [deleteOption, addPdfOption]
    }
    const options = [
      {
        label: t('common.pageProperties'),
        key: 'page_properties',
        icon: renderIcon(Settings01Icon),
      },
      {
        label: t('common.duplicatePage'),
        key: 'duplicate_page',
        icon: renderIcon(Copy01Icon),
      },
    ]
    if (((featureFlags.CONTENT_LIBRARY_ACCESSIBLE_TO_PUBLIC ?? false) || appStore.isInternal) && documentVersion.value > 1) {
      options.push(addToContentLibraryOption)
    }
    if (pages.value.length > 1) {
      options.push(deleteOption)
    }

    options.push(addPdfOption)

    return options
  }

  const addToContentLibrary = (page: IElement) => {
    activePage.value = page
    openAddToContentLibraryModal(true)
  }

  const openAddToContentLibraryModal = (val: boolean) => {
    showAddToContentLibraryModal.value = val
  }

  function onSelect(option: string, page: IElement) {
    switch (option) {
      case 'page_properties':
        store.updateActiveElement(page)
        setOpen()
        break
      case 'duplicate_page':
        store.duplicatePage(page.id)
        break
      case 'delete_page':
        store.deletePage(page.id)
        break
      case 'add_pdf':
        addPdfToPage(page.id)
        break
      case 'export_to_content_library':
        addToContentLibrary(page)
        break
    }
  }

  function addPdfToPage(pageId: string) {
    const index = pages.value.findIndex(p => p.id === pageId)
    if (index === -1) return
    appendImagesFromIndex.value = index
    showUploadModal.value = true
  }

  const onSelectoSelectEnd = (event: any) => {
    if (event.targets.length > 1) {
      if (event.selectedElements) setGroupTargetsOnSelection(event.selectedElements)
    }
    if (event.targets.length === 0) {
      setGroupTargetsOnSelection([])
    }
  }

  const onDrag = (styles: any, elementId: string) => {
    updateStyles('position', styles, elementId)
    updateOption('isGhost', true, elementId)
  }
  const onDragEnd = (options: any, elementId: string) => {
    store.moveFloatingElement(elementId as string, {
      elementId,
      newParentId: options.id,
    })
    store.setActiveGroupByElementId(elementId)
  }

  const onDropElement = (ev: DragEvent, index: number) => {
    isDragOver.value = false
    dragOverIndex.value = -1
    ev.preventDefault()
    const type = ev?.dataTransfer?.getData('name')
    if (type === CONTENT_LIBRARY) {
      onDropContentLibrary(ev, index)
      return
    }
  }

  const onDropContentLibrary = (evt: any, idx: number) => {
    const content = evt.dataTransfer.getData('content')
    const contentData = JSON.parse(content)
    actionBarStore.setDragState(false)
    store.addElementsFromContentLibraryToPage(
      {
        pages: contentData.pages,
        groups: contentData.groups || [],
        customValueLinkage: contentData.customValueLinkage || {},
      },
      idx
    )
  }

  const onDragOver = (event: any, index: number) => {
    event.preventDefault()
    isDragOver.value = true
    dragOverIndex.value = index
  }

  const onDragLeave = () => {
    isDragOver.value = false
    dragOverIndex.value = -1
  }
</script>

<template>
  <div class="pages-wrapper relative" ref="pageWrapperRef">
    <div class="absolute top-0">
      <div v-for="(page, index) in pages" :key="index" class="page">
        <div
          class="flex justify-between items-center justify-items-center"
          @drop="event => onDropElement(event, index - 1)"
          @dragover="event => onDragOver(event, index)"
          @dragleave="onDragLeave"
        >
          <div :style="`height: ${store.isDocumentEditable ? '0' : '30'}px;`" />
          <div class="flex items-center w-full my-4">
            <div
              class="h-px flex-grow mr-3 ml-6"
              :class="{
                'bg-blue-500': isDragOver && dragOverIndex === index,
              }"
            />
            <UIButton
              class="my-4"
              v-if="store.isDocumentEditable"
              id="add_new_page"
              circle
              quaternary
              size="large"
              @click="store.addPage(index - 1)"
            >
              <PlusIcon class="w-6 h-6 stroke-gray-500" />
            </UIButton>
            <div
              class="h-px flex-grow ml-3 mr-6"
              :class="{
                'bg-blue-500': isDragOver && dragOverIndex === index,
              }"
            />
          </div>
          <div
            v-if="store.isDocumentEditable"
            class="flex"
            :class="{
              'pointer-events-none opacity-50 cursor-not-allowed': page.component.options.fromPDF && pages.length <= 1,
            }"
          >
            <UIDropdown id="page_settings" :options="getOptions(page)" @select="val => onSelect(val, page)">
              <DotsHorizontalIcon class="w-6 h-6 stroke-gray-500 cursor-pointer" />
            </UIDropdown>
          </div>
        </div>

        <PageRenderer :id="`page-${page.id}`" :key="page.id" :elements="page.children" :page="page" :page-index="index" />
        <div
          :class="`flex items-center justify-items-center ${
            pages.length > 0 && index === pages.length - 1 ? 'justify-center' : 'justify-between'
          }`"
          v-if="pages.length > 0 && index === pages.length - 1"
          @drop="event => onDropElement(event, index + 1)"
          @dragover="event => onDragOver(event, index + 1)"
          @dragleave="onDragLeave"
        >
          <div :style="`height: ${store.isDocumentEditable ? '0' : '30'}px;`" />
          <div class="flex items-center w-full my-4">
            <div
              class="h-px flex-grow mr-3 ml-6"
              :class="{
                'bg-blue-500': isDragOver && index + 1 === dragOverIndex,
              }"
            />
            <UIButton
              class="my-4"
              v-if="store.isDocumentEditable"
              id="add_new_page"
              circle
              quaternary
              size="large"
              @click="store.addPage(index + 1)"
            >
              <PlusIcon class="w-6 h-6 stroke-gray-500" />
            </UIButton>
            <div
              class="h-px flex-grow ml-3 mr-6"
              :class="{
                'bg-blue-500': isDragOver && index + 1 === dragOverIndex,
              }"
            />
          </div>
          <div v-if="store.isDocumentEditable && pages.length > index + 1" class="flex">
            <UIDropdown id="page_settings" :options="getOptions(page)" @select="val => onSelect(val, page)">
              <DotsHorizontalIcon class="w-6 h-6 text-gray-700 cursor-pointer" />
            </UIDropdown>
          </div>
        </div>
      </div>
      <SelectoFieldComponent
        @on-selecto-select-end="onSelectoSelectEnd"
        @on-selecto-delete="deleteGroupElements"
        @on-selecto-clone="cloneGroupElements"
        @on-selecto-group="groupTargets"
        @on-selecto-ungroup="unGroupTargets"
        @on-selecto-clone-add-to-group="onCloneAndAddToGroupByGroupId"
        @on-drag-end="onDragEnd"
        @on-drag="onDrag"
        :active-group="activeGroup"
      />

      <AddToContentLibraryModal
        :show="showAddToContentLibraryModal"
        @on-cancel="openAddToContentLibraryModal(false)"
        @on-success="openAddToContentLibraryModal(false)"
        :is-from-page="true"
        :page-element="activePage"
        :groups="store.groups"
        :location-id="locationId"
        :is-internal="appStore.isInternal"
        :company-id="appStore.companyId"
      />
    </div>
    <AppendedFileUploadModal
      v-if="showUploadModal"
      :show="showUploadModal"
      @close-modal="showUploadModal = false"
      :is-append="true"
      :append-images-from-index="appendImagesFromIndex"
      :document-id="Array.isArray(route.params.documentId) ? route.params.documentId[0] : route.params.documentId"
    />
  </div>
</template>

<style lang="scss">
  .pages-wrapper {
    .page {
      &:last-child {
        margin-bottom: 30px;
      }
    }
  }
</style>
